"use client";

import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import posthog from "posthog-js";
import {
  fadeUp,
  staggerContainer,
  staggerItem,
  viewportOnce
} from "@/lib/motion";
import { Link } from "@/i18n/routing";
import { buttonClass } from "@/components/ui/Button";
import { PillBadge } from "@/components/ui/PillBadge";
import { SectionReveal } from "@/components/ui/SectionReveal";
import { PhoneIcon } from "@/components/ui/Icons/PhoneIcon";
import { EnvelopeIcon } from "@/components/ui/Icons/EnvelopeIcon";
import { AccentColor } from "@/lib/colors";
import { COMPANY, CONTACT_PEOPLE } from "@/lib/contact";

type ContactClickLocation = "contact_page_person" | "contact_page_general";

/**
 * The business question behind this page is whether it recovers outbound
 * answer rate, so every tel:/mailto: click is the metric that matters here —
 * not just the page view. `person` is omitted for the general enquiries block,
 * which has no individual owner.
 */
function trackContactClick(
  channel: "phone" | "email",
  location: ContactClickLocation,
  person?: string
) {
  posthog.capture("contact_click", { channel, location, person });
}

export function ContactContent() {
  const t = useTranslations("Contact");

  return (
    <>
      {/* ══════════ 1. HERO ══════════ */}
      <motion.section
        className="pt-6 lg:pt-32 pb-6 lg:pb-10 px-6"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeUp}
      >
        <div className="mx-auto max-w-3xl text-center">
          <PillBadge label={t("pill")} variant={AccentColor.Bubblegum} />
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-text-primary mt-6">
            {t("heroTitle")}
          </h1>
          <p className="mt-6 text-lg text-text-body leading-relaxed">
            {t("heroBody")}
          </p>
        </div>
      </motion.section>

      {/* ══════════ 2. PEOPLE ══════════ */}
      <SectionReveal>
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary">
            {t("peopleTitle")}
          </h2>
          <p className="mt-4 text-text-body leading-relaxed mx-auto max-w-2xl">
            {t("peopleBody")}
          </p>
        </div>
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {CONTACT_PEOPLE.map((person) => (
            <motion.div
              key={person.name}
              variants={staggerItem}
              className="flex h-full flex-col"
            >
              <div className="rounded-2xl overflow-hidden border border-border hover:border-border-hover transition-colors duration-200">
                <img
                  src={person.photo}
                  alt={person.name}
                  className="w-full aspect-square object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-1 flex-col pt-4 px-1">
                <h3 className="font-display text-xl font-bold tracking-tight text-text-primary">
                  {person.name}
                </h3>
                <p className="mt-1 text-text-muted text-xs font-body uppercase tracking-widest">
                  {person.role}
                </p>
                <div className="mt-3 space-y-2 text-sm">
                  {person.phone && person.phoneE164 && (
                    <a
                      href={`tel:${person.phoneE164}`}
                      onClick={() =>
                        trackContactClick(
                          "phone",
                          "contact_page_person",
                          person.name
                        )
                      }
                      className="flex items-center gap-2 text-text-body hover:text-text-primary transition-colors duration-200"
                    >
                      <PhoneIcon className="w-4 h-4 shrink-0" />
                      {person.phone}
                    </a>
                  )}
                  <a
                    href={`mailto:${person.email}`}
                    onClick={() =>
                      trackContactClick(
                        "email",
                        "contact_page_person",
                        person.name
                      )
                    }
                    className="flex items-center gap-2 text-text-body hover:text-text-primary transition-colors duration-200 break-all"
                  >
                    <EnvelopeIcon className="w-4 h-4 shrink-0" />
                    {person.email}
                  </a>
                </div>
                {/* mt-auto keeps this line on one baseline across the row,
                    even on Fanny's card which has no phone row. */}
                <p className="mt-3 pt-3 text-text-muted text-xs sm:mt-auto">
                  {t("languagesLabel")}: {t(`languages.${person.languagesKey}`)}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </SectionReveal>

      {/* ══════════ 3. GENERAL CONTACT + COMPANY DETAILS ══════════ */}
      <SectionReveal>
        <div className="grid md:grid-cols-2 gap-6">
          {/* General enquiries */}
          <div className="rounded-2xl border border-border p-8">
            <h2 className="font-display text-2xl font-bold tracking-tight text-text-primary">
              {t("generalTitle")}
            </h2>
            <p className="mt-3 text-text-body text-sm leading-relaxed">
              {t("generalBody")}
            </p>
            <dl className="mt-6 space-y-4 text-sm">
              <div>
                <dt className="text-text-muted text-xs font-body uppercase tracking-widest">
                  {t("phoneLabel")}
                </dt>
                <dd className="mt-1">
                  <a
                    href={`tel:${COMPANY.generalPhoneE164}`}
                    onClick={() =>
                      trackContactClick("phone", "contact_page_general")
                    }
                    className="text-text-body hover:text-text-primary transition-colors duration-200"
                  >
                    {COMPANY.generalPhone}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-text-muted text-xs font-body uppercase tracking-widest">
                  {t("emailLabel")}
                </dt>
                <dd className="mt-1">
                  <a
                    href={`mailto:${COMPANY.generalEmail}`}
                    onClick={() =>
                      trackContactClick("email", "contact_page_general")
                    }
                    className="text-text-body hover:text-text-primary transition-colors duration-200 break-all"
                  >
                    {COMPANY.generalEmail}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-text-muted text-xs font-body uppercase tracking-widest">
                  {t("openingHoursLabel")}
                </dt>
                <dd className="mt-1 text-text-body">{t("openingHours")}</dd>
              </div>
            </dl>
          </div>

          {/* Company details */}
          <div className="rounded-2xl border border-border p-8">
            <h2 className="font-display text-2xl font-bold tracking-tight text-text-primary">
              {t("companyTitle")}
            </h2>
            <p className="mt-3 text-text-body text-sm leading-relaxed">
              {t("companyBody")}
            </p>
            <dl className="mt-6 space-y-4 text-sm">
              <div>
                <dt className="text-text-muted text-xs font-body uppercase tracking-widest">
                  {t("addressLabel")}
                </dt>
                <dd className="mt-1 text-text-body">
                  {/* Values are identical on every locale — only the country
                      name is translated. The locality stays "Göteborg". */}
                  <address className="not-italic leading-relaxed">
                    {COMPANY.legalName}
                    <br />
                    {COMPANY.streetAddress}
                    <br />
                    {COMPANY.postalCode} {COMPANY.addressLocality}
                    <br />
                    {t("country")}
                  </address>
                </dd>
              </div>
              <div>
                <dt className="text-text-muted text-xs font-body uppercase tracking-widest">
                  {t("registrationLabel")}
                </dt>
                <dd className="mt-1 text-text-body">
                  {COMPANY.registrationNumber}
                </dd>
              </div>
              <div>
                <dt className="text-text-muted text-xs font-body uppercase tracking-widest">
                  {t("vatLabel")}
                </dt>
                <dd className="mt-1 text-text-body">{COMPANY.vatNumber}</dd>
              </div>
            </dl>
          </div>
        </div>
      </SectionReveal>

      {/* ══════════ 4. CTA ══════════ */}
      <SectionReveal>
        <div className="rounded-2xl border border-border p-8 md:p-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary">
            {t("ctaTitle")}
          </h2>
          <p className="mt-4 text-text-body leading-relaxed mx-auto max-w-2xl">
            {t("ctaBody")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {/* Locale-aware Link, not a bare <a>: "/" is only a meta-refresh
                stub, so a plain href would drop the #contact hash. */}
            <Link href="/#contact" className={buttonClass()}>
              {t("ctaPrimary")}
            </Link>
            <Link
              href="/about"
              className={buttonClass({ variant: "outlined" })}
            >
              {t("ctaSecondary")}
            </Link>
          </div>
        </div>
      </SectionReveal>
    </>
  );
}
